import xs, { Stream, Listener } from 'xstream';
import fromEvent from 'xstream/extra/fromEvent';
import { Dict, Driver, DSCustomElement } from '../core/core-types';
import { pick } from '../utility/helpers'

export interface IVanillaDOM {
  select(selector: string): IVanillaDOM;
  events(eventName: string): Stream<Event>;
  getProperties(props: string[]): Stream<Dict<any>[]>;
  getAttributes(attrs: string[]): Stream<Dict<any>[]>;
  getStyles(styles: string[]): Stream<Dict<any>[]>;
  setStyles(styles: Dict<any>): ChangeSet;
  setProperties(props: Dict<any>): ChangeSet;
  setAttributes(attrs: Dict<any>): ChangeSet;
}

export interface ChangeSet {
  type: 'attribute' | 'property' | 'style';
  selector: string;
  changes: Dict<any>;
}

export function makeVanillaDOMDriver(
  node: Element,
  options: Dict<any> = {}
): Driver<Stream<ChangeSet[]>, VanillaDOM> {
  const mergedOptions = Object.assign(
    {},
    { attributes: true, childList: true, subtree: true },
    options || {}
  );
  let updateOut = function(x: Element): void {
    return;
  };
  const _obs = new MutationObserver(() => {
    updateOut(node);
  });

  const updateProperties = (changes: Dict<any>) => (n: Element): void => {
    Object.assign(n, changes);
  };

  const updateAttributes = (changes: Dict<any>) => (n: Element): void => {
    Object.keys(changes).forEach((changeKey: string) => {
      n.setAttribute(changeKey, changes[changeKey]);
    });
  };

  const updateStyles = (changes: Dict<any>) => (n: HTMLElement): void => {
    const styleDeclaration = n.style;
    Object.keys(changes).forEach((changeKey: string) => {
      styleDeclaration.setProperty(changeKey, changes[changeKey]);
    });
  };

  const out$ = xs.createWithMemory<Element>({
    start(listener: Listener<Element>): void {
      updateOut = (n: Element) => listener.next(n);
      updateOut(node);
    },
    stop(): void {
      return;
    },
  });

  return sink$ => {
    sink$.subscribe({
      next(changeSets: ChangeSet[]): void {
        changeSets.forEach(changeSet => {
          const changeFn =
            (changeSet.type === 'property' && updateProperties) ||
            (changeSet.type === 'attribute' && updateAttributes) ||
            (changeSet.type === 'style' && updateStyles);

          const changeFnApplied = changeFn ? changeFn(changeSet.changes) : (el: HTMLElement) => {};
          const nodesToChange = Array.prototype.slice.call(
            node.querySelectorAll(changeSet.selector)
          );
          nodesToChange.forEach(changeFnApplied);
        });
      },
      error(): void {
        return;
      },
      complete(): void {
        return;
      },
    });

    _obs.observe(node, mergedOptions);

    const selector =`${node.localName}[data-guid="${(node as DSCustomElement).__guid}"]`;
    return new VanillaDOM(out$, selector);
  };
}

export class VanillaDOM implements IVanillaDOM {
  constructor(private base$: Stream<Element>, private selector: string) {}
  select(selector: string): VanillaDOM {
    const selectorString : string = selector.split(',').map(sel => `${this.selector} ${sel}`).join(', ');
    return new VanillaDOM(this.base$, selectorString);
  }

  events(eventName: string): Stream<Event> {
    return this.elements()
      .map(elements =>
        xs.merge<Event>(
          ...Array.prototype.slice.call(elements)
            .map((element: Element) => fromEvent(element, eventName))
        )
      )
      .flatten();
  }

  getProperties(props: string[]): Stream<Dict<any>[]> {
    return this.get(el => pick(props, el));
  }

  getAttributes(attrs: string[]): Stream<Dict<any>[]> {
    return this.get(el => pick(attrs, el.attributes));
  }

  setProperties(newProps: Dict<any>): ChangeSet {
    return { selector: this.selector, type: 'property', changes: newProps };
  }

  setAttributes(newAttrs: Dict<any>): ChangeSet {
    return {
      selector: this.selector,
      type: 'attribute',
      changes: newAttrs,
    };
  }

  getStyles(styles: string[]): Stream<Dict<any>[]> {
    return this.get((el: HTMLElement) => {
      const styleDeclaration = el.style;
      return styles.reduce((dict: Dict<any>, styleKey: string) => {
        dict[styleKey] = styleDeclaration.getPropertyValue(styleKey);
        return dict;
      }, {});
    });
  }

  setStyles(newStyles: Dict<any>): ChangeSet {
    return { selector: this.selector, type: 'style', changes: newStyles };
  }

  private elements(): Stream<NodeListOf<HTMLElement>> {
    return this.base$.map(node => node.querySelectorAll<HTMLElement>(this.selector || '*'));
  }

  private get<T>(fn: (element: HTMLElement) => T): Stream<T[]> {
    return this.elements().map(elements =>
      Array.prototype.slice.call(elements).map((element) => fn(element))
    );
  }
}
